/**
 * O AGREGADO DO MÊS do financeiro — o número do hero da aba "Visão geral" e os
 * KPIs logo abaixo dele.
 *
 * Vive fora do componente por dois motivos, e o segundo é o que importa:
 *
 * **(a) É regra de negócio, e regra de negócio se testa.** "O que entra no
 * saldo do mês" decide o número mais visível da tela.
 *
 * **(b) Hero e KPI não podem discordar.** O hero afirma "Saldo do mês" em
 * `text-3xl`; se ele e o cartão abaixo fossem duas implementações da mesma
 * conta, bastaria uma correção num dos dois para a tela se contradizer sozinha,
 * sem erro nenhum (é o modo de falha do #82 e do #69). Aqui a conta é UMA, e
 * por isso o cartão "Saldo do mês" saiu da grade: repetir o mesmo número duas
 * vezes com dois centímetros de distância não informa nada, e o lugar liberado
 * ganhou o "A pagar (7d)", que espelha o "A receber (7d)" e não existia.
 *
 * ─── AS DUAS DECISÕES QUE ERRAM EM SILÊNCIO ─────────────────────────────────
 *
 * · **Lançamento CANCELADO fica fora de todas as somas.** Ele é o análogo do
 *   orçamento cancelado (#188): dinheiro que não vai entrar (nem sair). Somá-lo
 *   ao saldo diria ao prestador que ele tem mais na mão do que tem. Quantos
 *   ficaram fora é DEVOLVIDO para a tela dizer — "no silent caps" (#61). Isto
 *   CORRIGE o comportamento anterior do KPI, que somava o cancelado junto.
 *
 * · **O recorte do mês é por `due_date`, e a comparação é de STRING.** As datas
 *   do financeiro são `DATE` ('YYYY-MM-DD') e são comparadas como texto — a
 *   fronteira com `Date` passa por `todayIsoDate`, que lê o calendário LOCAL.
 *   `new Date(due_date)` é meia-noite UTC e em UTC-3 cai no DIA ANTERIOR
 *   (#36/#114), o que joga o vencimento do dia 1º para o mês passado.
 */
import { endOfMonth, startOfMonth, addDays } from 'date-fns';
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';
import type { FinanceStatus } from '@/types/database';

/** Quantos dias à frente contam como "vence logo". */
export const DUE_SOON_DAYS = 7;

/**
 * O que este módulo enxerga de um lançamento.
 *
 * As linhas chegam da view `finance_entries_with_status` (que traz o
 * `computed_status` do servidor) e do cache offline, tipadas como `any` — nada
 * aqui é lido sem cuidado.
 */
export interface FinanceOverviewRow {
  kind?: string | null;
  amount?: unknown;
  due_date?: string | null;
  status?: string | null;
  computed_status?: string | null;
}

export interface FinanceMonthSummary {
  /** Entradas do mês (fora as canceladas). */
  income: number;
  /** Saídas do mês (fora as canceladas). */
  expense: number;
  /** `income - expense`. É PREVISÃO de fechamento, não caixa (#187). */
  balance: number;
  /** Entradas do mês já QUITADAS (`status === 'paid'`, nunca `computed_status`). */
  received: number;
  /** Entradas em aberto que vencem nos próximos {@link DUE_SOON_DAYS} dias. */
  dueSoonIncome: number;
  /** Saídas em aberto que vencem nos próximos {@link DUE_SOON_DAYS} dias. */
  dueSoonExpense: number;
  /** Tudo que está VENCIDO — entrada e saída, sem recorte de mês. */
  overdue: number;
  /** Lançamentos do mês que ficaram FORA das somas por estarem cancelados. */
  excludedCount: number;
}

/**
 * O status EFETIVO de um lançamento.
 *
 * `computed_status` vem da view e é a autoridade (é ele que detecta atraso no
 * servidor); sem ele — linha do cache antiga, ou criada offline — a conta é
 * refeita localmente. Nunca `Boolean(status)`: o vocabulário tem quatro
 * valores, não dois.
 */
export function effectiveFinanceStatus(
  entry: { status?: string | null; due_date?: string | null; computed_status?: string | null },
  today: string = todayIsoDate(),
): FinanceStatus {
  const fromView = entry?.computed_status;
  if (fromView) return fromView as FinanceStatus;
  return computeFinanceStatus(
    (entry?.status ?? 'pending') as FinanceStatus,
    String(entry?.due_date ?? today),
    today,
  );
}

function amountOf(entry: FinanceOverviewRow): number {
  const parsed = Number(entry?.amount ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * O agregado do mês corrente, a partir da lista COMPLETA de lançamentos.
 *
 * `now` é parâmetro para o teste poder congelar o calendário: um fixture que
 * fala em "este mês" sem relógio fixo é sorte, não fixture (#154).
 */
export function summarizeFinanceMonth(
  entries: readonly FinanceOverviewRow[],
  now: Date = new Date(),
): FinanceMonthSummary {
  const today = todayIsoDate(now);
  const monthStart = todayIsoDate(startOfMonth(now));
  const monthEnd = todayIsoDate(endOfMonth(now));
  const soonEnd = todayIsoDate(addDays(now, DUE_SOON_DAYS));

  let income = 0;
  let expense = 0;
  let received = 0;
  let dueSoonIncome = 0;
  let dueSoonExpense = 0;
  let overdue = 0;
  let excludedCount = 0;

  for (const entry of entries) {
    const due = String(entry?.due_date ?? '');
    const status = effectiveFinanceStatus(entry, today);
    const inMonth = due >= monthStart && due <= monthEnd;

    if (status === 'canceled') {
      if (inMonth) excludedCount += 1;
      continue;
    }

    const amount = amountOf(entry);
    const isIncome = entry?.kind === 'income';

    if (inMonth) {
      if (isIncome) {
        income += amount;
        // Quitado é `status === 'paid'`, NUNCA `computed_status` — este último é
        // a coluna derivada que existe para detectar ATRASO, e usá-la aqui faria
        // um título que vence amanhã entrar no dinheiro que já entrou (#187).
        if (entry?.status === 'paid') received += amount;
      } else {
        expense += amount;
      }
    }

    if (status === 'pending' && due >= today && due <= soonEnd) {
      if (isIncome) dueSoonIncome += amount;
      else dueSoonExpense += amount;
    }

    if (status === 'overdue') overdue += amount;
  }

  return {
    income,
    expense,
    balance: income - expense,
    received,
    dueSoonIncome,
    dueSoonExpense,
    overdue,
    excludedCount,
  };
}
